import type { NavLink } from "@site-factory/config";
import { Icon } from "../icons";
import { AnimatedIcon } from "../components/AnimatedIcon";
import { cn } from "../utils";
import { MainContainer } from "./MainContainer";

export interface SiteFooterProps {
  /** Site name */
  siteName: string;
  /** Copyright text */
  copyright?: string;
  /** Footer navigation links */
  links?: NavLink[];
  /** Social media links */
  socials?: Array<{ platform: string; href: string; icon?: string }>;
  /** Location-specific details */
  locations?: Array<{
    name: string;
    address: string;
    facebook?: string;
    instagram?: string;
  }>;
  /** Locations group label */
  locationsLabel?: string;
  /** Grouped location details by region/area */
  locationGroups?: Array<{
    label: string;
    locations: Array<{
      name: string;
      address: string;
    }>;
    socials?: Array<{ platform: string; href: string; icon?: string }>;
  }>;
  /** Contact information */
  contact?: {
    phone?: { label: string; href: string };
    email?: { label: string; href: string };
    address?: string;
  };
  /** Additional CSS classes */
  className?: string;
  /** Optional link component (e.g., react-router Link adapter) */
  linkComponent?: (props: {
    href: string;
    className?: string;
    children: React.ReactNode;
  }) => JSX.Element;
}

export function SiteFooter({
  siteName,
  copyright,
  links,
  socials,
  locations,
  locationsLabel,
  locationGroups,
  contact,
  className,
  linkComponent,
}: SiteFooterProps) {
  const LinkComponent =
    linkComponent ??
    ((props: { href: string; className?: string; children: React.ReactNode }) => (
      <a {...props} />
    ));
  const bottomLinks =
    links && links.length > 0
      ? links
      : [
          { label: "Privacy Policy", href: "/privacy" },
          { label: "Contact", href: "/contact" },
        ];
  const currentYear = new Date().getFullYear();
  const copyrightText = copyright || `© ${currentYear} ${siteName}. All rights reserved.`;
  const hasGroupedLocations = locationGroups && locationGroups.length > 0;
  const sharedSocials = !hasGroupedLocations && socials && socials.length > 0 ? socials : null;
  const hasLocationSocials = locations?.some((location) => location.facebook || location.instagram);
  const renderSocialIcons = (
    items: Array<{ platform: string; href: string; icon?: string }>,
    labelPrefix?: string,
    sizeClass = "w-6 h-6",
    iconClass = "w-[12px] h-[12px]"
  ) => (
    <div className="flex items-center gap-2">
      {items.map((social) => {
        const platform = social.platform.toLowerCase();
        const isInstagram = platform.includes("instagram") || platform.includes("insta");
        const isFacebook = platform.includes("facebook") || platform.includes("fb");
        const icon = isInstagram ? (
          <Icon name="instagram" className={iconClass} />
        ) : isFacebook ? (
          <Icon name="facebook" className={iconClass} />
        ) : (
          <span className="text-[10px] font-semibold">{social.platform.slice(0, 2).toUpperCase()}</span>
        );
        const bgClass = isInstagram
          ? "bg-gradient-to-tr from-[#f58529] via-[#dd2a7b] to-[#515bd4]"
          : isFacebook
            ? "bg-[#1877F2]"
            : "bg-neutral-300 text-neutral-700";

        return (
          <a
            key={`${labelPrefix ?? "social"}-${social.platform}`}
            href={social.href}
            className={`flex items-center justify-center ${sizeClass} rounded-full text-white shadow-sm hover:shadow-md transition-all duration-200 ${bgClass}`}
            aria-label={labelPrefix ? `${labelPrefix} ${social.platform}` : social.platform}
            target="_blank"
            rel="noopener noreferrer"
          >
            <AnimatedIcon variant="scale" className="flex items-center justify-center">
              {icon}
            </AnimatedIcon>
          </a>
        );
      })}
    </div>
  );

  return (
    <footer className={cn("w-full bg-gradient-to-br from-primary/5 via-background to-secondary/5 border-t border-neutral-200 py-8", className)}>
      <MainContainer>
        {hasGroupedLocations ? (
          <div className="grid gap-8 md:grid-cols-[0.9fr_1.15fr_1.15fr] md:gap-10 lg:gap-12">
            {/* Contact Information */}
            {contact && (
              <div className="space-y-2 text-xs sm:space-y-3 sm:text-sm">
                <p className="text-xs font-semibold uppercase tracking-wide text-neutral-500">Contact</p>
              {contact.phone && (
                <a
                  href={contact.phone.href}
                  className="flex items-center gap-2 text-neutral-600 hover:text-primary transition-colors group"
                >
                  <AnimatedIcon variant="scale" className="text-primary">
                    <Icon name="phone" className="w-4 h-4" />
                  </AnimatedIcon>
                  <span className="group-hover:underline">{contact.phone.label}</span>
                </a>
              )}
              {contact.email && (
                <a
                  href={contact.email.href}
                  className="flex items-center gap-2 text-neutral-600 hover:text-primary transition-colors group"
                >
                  <AnimatedIcon variant="scale" className="text-primary">
                    <Icon name="mail" className="w-4 h-4" />
                  </AnimatedIcon>
                  <span className="group-hover:underline">{contact.email.label}</span>
                </a>
              )}
              </div>
            )}

            {locationGroups?.map((group) => (
              <div key={group.label} className="space-y-3 text-xs sm:text-sm">
                <p className="text-xs font-semibold uppercase tracking-wide text-neutral-500">{group.label}</p>
                <div className="space-y-2">
                  {group.locations.map((location) => (
                    <p key={location.name} className="flex items-start gap-2 text-neutral-600">
                      <AnimatedIcon variant="scale" className="text-primary mt-0.5">
                        <Icon name="map-pin" className="w-4 h-4" />
                      </AnimatedIcon>
                      <span>
                        <span className="font-medium text-neutral-700">{location.name}</span>
                        <span className="text-neutral-500"> · </span>
                        <span>{location.address}</span>
                      </span>
                    </p>
                  ))}
                </div>
                {group.socials && group.socials.length > 0 && (
                  <div className="pt-1">
                    {renderSocialIcons(group.socials, group.label, "w-6 h-6", "w-[12px] h-[12px]")}
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-3 gap-6 sm:gap-12">
            {/* Contact Information */}
            {contact && (
              <div className="order-1 space-y-2 text-xs sm:space-y-3 sm:text-sm">
                <p className="text-xs font-semibold uppercase tracking-wide text-neutral-500">Contact</p>
                {contact.phone && (
                  <a
                    href={contact.phone.href}
                    className="flex items-center gap-2 text-neutral-600 hover:text-primary transition-colors group"
                  >
                    <Icon name="phone" className="w-4 h-4 text-primary" />
                    <span className="group-hover:underline">{contact.phone.label}</span>
                  </a>
                )}
                {contact.email && (
                  <a
                    href={contact.email.href}
                    className="flex items-center gap-2 text-neutral-600 hover:text-primary transition-colors group"
                  >
                    <Icon name="mail" className="w-4 h-4 text-primary" />
                    <span className="group-hover:underline">{contact.email.label}</span>
                  </a>
                )}
              </div>
            )}

            {/* Locations */}
            {(locations && locations.length > 0) && (
              <div className="order-2 space-y-2 text-xs sm:space-y-3 sm:text-sm">
                <p className="text-xs font-semibold uppercase tracking-wide text-neutral-500">
                  {locationsLabel ?? "Locations"}
                </p>
                <div className="space-y-3">
                  {locations.map((location) => (
                    <div key={location.name} className="space-y-1">
                      <p className="font-semibold text-neutral-700">{location.name}</p>
                      <p className="flex items-start gap-2 text-neutral-600">
                        <Icon name="map-pin" className="w-4 h-4 text-primary mt-0.5" />
                        <span>{location.address}</span>
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Social Media */}
            {(sharedSocials || hasLocationSocials) && (
              <div className="order-3 space-y-2 text-xs sm:space-y-3 sm:text-sm">
                <p className="text-xs font-semibold uppercase tracking-wide text-neutral-500">Socials</p>
                <div className="space-y-3">
                  {sharedSocials ? (
                    renderSocialIcons(sharedSocials)
                  ) : (
                    locations?.map((location) => {
                      const hasSocials = location.facebook || location.instagram;
                      if (!hasSocials) return null;

                      return (
                        <div key={location.name} className="space-y-2">
                          <p className="font-semibold text-neutral-700">{location.name}</p>
                          <div className="flex items-center gap-3">
                            {location.facebook && (
                              <a
                                href={location.facebook}
                                className="flex items-center justify-center w-8 h-8 rounded-full bg-[#1877F2] text-white shadow-sm hover:shadow-md transition-all duration-200"
                                aria-label={`${location.name} Facebook`}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                <AnimatedIcon variant="scale" className="flex items-center justify-center">
                                  <Icon name="facebook" className="w-[14px] h-[14px]" />
                                </AnimatedIcon>
                              </a>
                            )}
                            {location.instagram && (
                              <a
                                href={location.instagram}
                                className="flex items-center justify-center w-8 h-8 rounded-full bg-gradient-to-tr from-[#f58529] via-[#dd2a7b] to-[#515bd4] text-white shadow-sm hover:shadow-md transition-all duration-200"
                                aria-label={`${location.name} Instagram`}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                <AnimatedIcon variant="scale" className="flex items-center justify-center">
                                  <Icon name="instagram" className="w-[14px] h-[14px]" />
                                </AnimatedIcon>
                              </a>
                            )}
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Bottom Bar */}
        <div className="mt-6 pt-6 border-t border-neutral-200">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 text-xs sm:text-sm text-neutral-600">
            <p>{copyrightText}</p>
            <nav className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1">
              {bottomLinks.map((link, index) => (
                <span key={link.href} className="flex items-center gap-3">
                  {index > 0 && <span aria-hidden="true">•</span>}
                  <LinkComponent
                    href={link.href}
                    className="hover:text-primary transition-colors hover:underline"
                  >
                    {link.label}
                  </LinkComponent>
                </span>
              ))}
            </nav>
          </div>
        </div>
      </MainContainer>
    </footer>
  );
}
